<template>
  <v-card outlined class="groups-metrics-table">
    <div class="groups-metrics-table__header">
      <div class="groups-metrics-table__title">
        <v-icon small class="mr-2">mdi-folder-multiple-outline</v-icon>
        Analysis groups
        <span class="caption grey--text ml-2">
          Latest snapshot per group · select two groups to compare them
        </span>
      </div>
      <v-spacer />
      <v-btn
        small
        outlined
        color="primary"
        class="groups-metrics-table__compare"
        :disabled="selected.length !== 2"
        @click="
          $emit(
            'compare',
            selected.map((g) => g.id)
          )
        "
      >
        <v-icon small left>mdi-compare-horizontal</v-icon>
        Compare{{ selected.length === 2 ? '' : ' (select 2)' }}
      </v-btn>
    </div>

    <v-skeleton-loader v-if="loading" type="table-row@3" class="mx-2" />

    <div v-else-if="!groups.length" class="groups-metrics-table__empty">
      <v-icon size="40" color="grey lighten-1">mdi-folder-plus-outline</v-icon>
      <div class="subtitle-2 grey--text mt-2">No groups yet</div>
      <v-btn small text color="primary" to="/analysis-groups">Create group</v-btn>
    </div>

    <v-data-table
      v-else
      v-model="selected"
      :headers="headers"
      :items="rows"
      item-key="id"
      show-select
      dense
      hide-default-footer
      :items-per-page="-1"
      class="groups-metrics-table__table"
      @click:row="(row) => $emit('select-scope', row.id)"
    >
      <template #[`header.data-table-select`]>
        <span></span>
      </template>
      <template #[`item.data-table-select`]="{ isSelected, select }">
        <v-simple-checkbox
          :value="isSelected"
          :disabled="!isSelected && selected.length >= 2"
          :ripple="false"
          @input="select($event)"
          @click.stop
        />
      </template>
      <template #[`item.name`]="{ item }">
        <div class="groups-metrics-table__name">
          <strong>{{ item.name }}</strong>
          <v-chip v-if="item.id === activeGroupId" x-small color="primary" dark class="ml-2">
            scope
          </v-chip>
        </div>
        <div class="caption grey--text">
          {{ item.config_label }}
          <span
            v-if="item.frozen_at"
            title="Frozen: configuration and engines can no longer change"
          >
            · frozen
          </span>
        </div>
      </template>
      <template #[`item.members`]="{ item }">
        {{ item.members }}
      </template>
      <template v-for="metric in metrics" #[`item.${metric.key}`]="{ item }">
        <span
          :key="metric.key"
          class="groups-metrics-table__metric"
          :class="`${metricColor(metric.key, item[metric.key])}--text`"
        >
          {{ formatPct(item[metric.key]) }}
        </span>
      </template>
      <template #[`item.engines`]="{ item }">
        <v-chip v-for="engine in item.engines" :key="engine" x-small outlined label class="mr-1">
          {{ engine }}
        </v-chip>
      </template>
      <template #[`item.last_snapshot_at`]="{ item }">
        <span class="text-no-wrap">{{ item.last_snapshot_at || '—' }}</span>
      </template>
      <template #[`item.actions`]="{ item }">
        <v-btn icon x-small title="Open group" :to="`/analysis-groups/${item.id}`" @click.stop>
          <v-icon small>mdi-open-in-new</v-icon>
        </v-btn>
        <v-btn icon x-small title="Set as scope" @click.stop="$emit('select-scope', item.id)">
          <v-icon small>mdi-crosshairs-gps</v-icon>
        </v-btn>
      </template>
    </v-data-table>
  </v-card>
</template>

<script>
import {
  SERIES_METRICS,
  metricStatus,
  metricStatusColor,
  metricValue,
} from '@/utils/metricsSeries';

/**
 * Every analysis group with the three PO metrics of its latest snapshot (M4 screen 4). Clicking a
 * row sets it as the evaluation scope; selecting two rows enables Compare.
 */
export default {
  name: 'GroupsMetricsTable',

  props: {
    /** Items of `GET /analysis-groups` (with `members` count and `last_snapshot`). */
    groups: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    activeGroupId: { type: [Number, String], default: null },
  },

  data() {
    return {
      selected: [],
      metrics: SERIES_METRICS,
    };
  },

  computed: {
    headers() {
      return [
        { text: 'Group', value: 'name', sortable: true },
        { text: 'Videos', value: 'members', width: 90, align: 'end' },
        ...SERIES_METRICS.map((metric) => ({
          text: metric.label,
          value: metric.key,
          width: 120,
          align: 'end',
        })),
        { text: 'Engines', value: 'engines', sortable: false },
        { text: 'Last snapshot', value: 'last_snapshot_at', width: 150 },
        { text: '', value: 'actions', sortable: false, width: 80, align: 'end' },
      ];
    },
    rows() {
      return this.groups.map((group) => {
        const snapshot = group.last_snapshot || null;
        const row = {
          id: Number(group.id),
          name: group.name,
          frozen_at: group.frozen_at || null,
          members: Number(group.members ?? group.counts?.members ?? 0),
          engines: Array.isArray(group.engines) ? group.engines : [],
          config_label: group.config_version
            ? `config v${group.config_version}`
            : 'config: current',
          last_snapshot_at:
            snapshot && snapshot.computed_at ? this.formatDate(snapshot.computed_at) : '',
        };
        SERIES_METRICS.forEach((metric) => {
          row[metric.key] = snapshot ? metricValue(snapshot, metric.key) : null;
        });
        return row;
      });
    },
  },

  methods: {
    formatPct(value) {
      return value === null || value === undefined ? '—' : `${value}%`;
    },
    metricColor(key, value) {
      return metricStatusColor(metricStatus(key, value));
    },
    formatDate(value) {
      const date = new Date(value);
      if (!Number.isFinite(date.getTime())) return String(value);
      const pad = (n) => String(n).padStart(2, '0');
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(
        date.getHours()
      )}:${pad(date.getMinutes())}`;
    },
  },
};
</script>

<style scoped>
.groups-metrics-table {
  border-radius: 10px !important;
  margin-bottom: 32px;
}

.groups-metrics-table__header {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  padding: 12px 16px 4px;
}

.groups-metrics-table__title {
  display: flex;
  align-items: center;
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #555;
}

.groups-metrics-table__table {
  background: transparent !important;
}

.groups-metrics-table__table tbody tr {
  cursor: pointer;
}

.groups-metrics-table__metric {
  font-weight: 700;
}

.groups-metrics-table__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 16px;
  text-align: center;
}
</style>
